import PlanDTO from "../Models/DTOs/Plan";
import { PlanPeriod } from "../Models/Enums/PlanPeriod";
import { SubscriptionOffer } from "../Models/DTOs/SubscriptionOffer";
import { getResolvedOffer, isPrepaidOffer } from "./BillingOffers";

export const MONTHLY_MARKETING_PLAN_KEY = "PLAN_TRUTH_SEEKER";
export const ANNUAL_MARKETING_PLAN_KEY = "PLAN_GAME_CHANGER";

export type MarketingPriceSource = "promotion" | "intro" | "base";

export interface MarketingPlanPricing {
    currency: string;
    initialPrice: number;
    renewalPrice: number;
    discountDurationMonths: number | null;
    source: MarketingPriceSource;
    /** Authoritative catalog terms, absent only for legacy or regular pricing. */
    offer?: SubscriptionOffer;
}

export interface AnnualPlanSavings {
    currency: string;
    firstYearSavings: number | null;
    renewalYearSavings: number | null;
}

export function getMonthlyRenewalCopy(
    pricing: MarketingPlanPricing | null,
    initialPrice: string | null,
    renewalPrice: string | null
): string | null {
    if (
        !pricing ||
        !initialPrice ||
        !renewalPrice
    ) {
        return null;
    }

    const offer = pricing.offer;
    if (isPrepaidOffer(offer)) {
        const count = offer?.intro_interval_count;
        const interval = offer?.intro_interval;
        const period = count && interval
            ? `${count} ${interval}${count === 1 ? "" : "s"}`
            : "your introductory period";
        return `${initialPrice} once for ${period}, then ${renewalPrice}/month.`;
    }

    if (offer?.duration === "forever") {
        return `${initialPrice}/month ongoing.`;
    }

    if (pricing.initialPrice === pricing.renewalPrice) return null;

    if (offer?.duration === "once") {
        return `Your first month for ${initialPrice}, then ${renewalPrice}/month.`;
    }

    if (offer?.duration === "repeating" && offer.duration_months) {
        return `${initialPrice}/month for ${offer.duration_months} ${offer.duration_months === 1 ? "month" : "months"}, then ${renewalPrice}/month.`;
    }

    if (pricing.source === "promotion") {
        return `Limited-time price ${initialPrice}. Standard renewal is ${renewalPrice}/month.`;
    }

    return pricing.discountDurationMonths === 1
        ? `Your first month for ${initialPrice}, then ${renewalPrice}/month.`
        : `Your first ${pricing.discountDurationMonths} months for ${initialPrice}/month, then ${renewalPrice}/month.`;
}

const isUsablePrice = (price: unknown): price is number =>
    typeof price === "number" && Number.isFinite(price) && price >= 0;

const hasDiscountMapping = (
    plan: PlanDTO,
    source: "promotion" | "intro"
): boolean =>
    plan.paymentProviders?.some((provider) => {
        if (provider.providerName?.trim().toLowerCase() !== "stripe") {
            return false;
        }

        if (source === "promotion") {
            return Boolean(provider.campaign?.campaignCouponId?.trim());
        }

        return Boolean(provider.standard?.stdCouponId?.trim());
    }) ?? false;

export function selectMonthlyMarketingPlan(
    plans: PlanDTO[] | null | undefined
): PlanDTO | null {
    if (!Array.isArray(plans) || plans.length === 0) return null;

    const keyedMonthlyPlan = plans.find(
        (plan) =>
            plan.key === MONTHLY_MARKETING_PLAN_KEY &&
            plan.period === PlanPeriod.Monthly
    );
    if (keyedMonthlyPlan) return keyedMonthlyPlan;

    return plans
        .filter((plan) => plan.period === PlanPeriod.Monthly)
        .reduce<PlanDTO | null>((selected, plan) => {
            if (!selected || plan.sortOrder < selected.sortOrder) return plan;
            return selected;
        }, null);
}

export function selectAnnualMarketingPlan(
    plans: PlanDTO[] | null | undefined
): PlanDTO | null {
    if (!Array.isArray(plans) || plans.length === 0) return null;

    const keyedAnnualPlan = plans.find(
        (plan) =>
            plan.key === ANNUAL_MARKETING_PLAN_KEY &&
            plan.period === PlanPeriod.Yearly
    );
    if (keyedAnnualPlan) return keyedAnnualPlan;

    return plans
        .filter((plan) => plan.period === PlanPeriod.Yearly)
        .reduce<PlanDTO | null>((selected, plan) => {
            if (!selected || plan.sortOrder < selected.sortOrder) return plan;
            return selected;
        }, null);
}

export function resolveMarketingPlanPricing(
    plan: PlanDTO | null | undefined
): MarketingPlanPricing | null {
    if (!plan || !isUsablePrice(plan.price) || !plan.currency) return null;

    const offer = getResolvedOffer(plan);
    if (
        offer &&
        isUsablePrice(offer.final_amount_minor) &&
        isUsablePrice(offer.base_amount_minor) &&
        offer.currency
    ) {
        return {
            currency: offer.currency,
            initialPrice: offer.final_amount_minor,
            renewalPrice: !isPrepaidOffer(offer) && offer.duration === "forever"
                ? offer.final_amount_minor
                : offer.base_amount_minor,
            discountDurationMonths: isPrepaidOffer(offer)
                ? null
                : offer.duration === "once"
                    ? offer.cadence === "year" ? 12 : 1
                    : offer.duration === "repeating" ? offer.duration_months ?? null : null,
            source: "promotion",
            offer,
        };
    }

    // A successful catalog response is authoritative, including "no offer".
    // Never revive stale legacy offers (or malformed catalog terms) here.
    if (plan.offerCatalogResolved || offer) {
        return {
            currency: plan.currency,
            initialPrice: plan.price,
            renewalPrice: plan.price,
            discountDurationMonths: 0,
            source: "base",
        };
    }

    if (
        plan.promotionIsActive &&
        isUsablePrice(plan.promotionPrice) &&
        hasDiscountMapping(plan, "promotion")
    ) {
        return {
            currency: plan.currency,
            initialPrice: plan.promotionPrice,
            renewalPrice: plan.price,
            // Campaign duration is not exposed by the public plans API. Keep
            // the marketing copy accurate by treating it as unspecified.
            discountDurationMonths: null,
            source: "promotion",
        };
    }

    const introPrice = plan.standardDiscount?.stdPromoPrice;
    if (isUsablePrice(introPrice) && hasDiscountMapping(plan, "intro")) {
        const configuredDuration =
            plan.standardDiscount?.stdPromoDurationMonths;
        const discountDurationMonths =
            typeof configuredDuration === "number" && configuredDuration > 0
                ? Math.floor(configuredDuration)
                : 1;

        return {
            currency: plan.currency,
            initialPrice: introPrice,
            renewalPrice: plan.price,
            discountDurationMonths,
            source: "intro",
        };
    }

    return {
        currency: plan.currency,
        initialPrice: plan.price,
        renewalPrice: plan.price,
        discountDurationMonths: 0,
        source: "base",
    };
}

export function resolveAnnualPlanSavings(
    monthlyPlan: PlanDTO | null | undefined,
    annualPlan: PlanDTO | null | undefined
): AnnualPlanSavings | null {
    if (
        monthlyPlan?.period !== PlanPeriod.Monthly ||
        annualPlan?.period !== PlanPeriod.Yearly
    ) {
        return null;
    }

    const monthlyPricing = resolveMarketingPlanPricing(monthlyPlan);
    const annualPricing = resolveMarketingPlanPricing(annualPlan);

    if (
        !monthlyPricing ||
        !annualPricing ||
        monthlyPricing.currency.trim().toUpperCase() !==
            annualPricing.currency.trim().toUpperCase()
    ) {
        return null;
    }

    const renewalYearDifference =
        (monthlyPricing.offer?.base_amount_minor ?? monthlyPricing.renewalPrice) * 12 -
        (annualPricing.offer?.base_amount_minor ?? annualPricing.renewalPrice);
    const renewalYearSavings =
        renewalYearDifference > 0 ? renewalYearDifference : null;

    // Custom prepaid periods and catalog coupons can cross annual boundaries.
    // Retain only the explicit standard-price comparison for catalog offers,
    // rather than comparing unequal access periods or implying every following
    // year has already returned to regular billing. Legacy campaign durations
    // are unknown and also cannot support a first-year comparison.
    const canCalculateMonthlyFirstYear =
        !monthlyPricing.offer && !annualPricing.offer &&
        (monthlyPricing.source !== "promotion" ||
        monthlyPricing.initialPrice === monthlyPricing.renewalPrice);
    let firstYearSavings: number | null = null;

    if (canCalculateMonthlyFirstYear) {
        const introductoryMonths =
            monthlyPricing.source === "intro"
                ? Math.min(
                      Math.max(monthlyPricing.discountDurationMonths ?? 0, 0),
                      12
                  )
                : 0;
        const monthlyFirstYearCost =
            monthlyPricing.initialPrice * introductoryMonths +
            monthlyPricing.renewalPrice * (12 - introductoryMonths);
        const firstYearDifference =
            monthlyFirstYearCost - annualPricing.initialPrice;

        firstYearSavings =
            firstYearDifference > 0 ? firstYearDifference : null;
    }

    if (firstYearSavings === null && renewalYearSavings === null) return null;

    return {
        currency: annualPricing.currency,
        firstYearSavings,
        renewalYearSavings,
    };
}
